import React, { useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate, formatDateTime, packDisplay } from "@/lib/format";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, AlertTriangle, Clock, MinusCircle, Search, Wrench, Scale } from "lucide-react";
import { toast } from "sonner";

const STATUS_UI = {
  sinkron: { label: "SINKRON", cls: "bg-emerald-50 border-emerald-300", iconCls: "bg-emerald-500", textCls: "text-emerald-800", Icon: CheckCircle2 },
  perlu_pemeriksaan: { label: "PERLU PEMERIKSAAN — ADA SELISIH", cls: "bg-rose-50 border-rose-300", iconCls: "bg-rose-500", textCls: "text-rose-800", Icon: AlertTriangle },
  pending_approval: { label: "MENUNGGU PERSETUJUAN", cls: "bg-amber-50 border-amber-300", iconCls: "bg-amber-500", textCls: "text-amber-800", Icon: Clock },
  belum_verifikasi: { label: "BELUM TERVERIFIKASI", cls: "bg-slate-50 border-slate-300", iconCls: "bg-slate-400", textCls: "text-slate-700", Icon: MinusCircle },
};

const ACTION_LABEL = {
  relink: "Movement ditautkan ulang",
  generate: "Missing movement dibuat ulang",
  rebuild: "Ledger dibangun ulang",
  delete: "Orphan/duplikat dihapus",
  mark_review: "Ditandai untuk review manual",
};

export default function Reconciliation() {
  const { user } = useContext(AuthContext);
  const canRepair = ["owner", "manager", "admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [salesList, setSalesList] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [location, setLocation] = useState("armada");
  const [salesId, setSalesId] = useState("");
  const [productId, setProductId] = useState("");
  const [category, setCategory] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [causes, setCauses] = useState(null);
  const [causesOpen, setCausesOpen] = useState(false);
  const [causesLoading, setCausesLoading] = useState(false);
  const [repairPlan, setRepairPlan] = useState(null);
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const load = useCallback(async () => {
    const params = { location };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (location === "armada" && salesId) params.sales_id = salesId;
    if (productId) params.product_id = productId;
    if (category) params.category = category;
    if (customerId) params.customer_id = customerId;
    const r = await api.get("/reconciliation", { params });
    setData(r.data);
    return r.data;
  }, [dateFrom, dateTo, location, salesId, productId, category, customerId]);

  useEffect(() => { load().catch((e) => { console.error(e); toast.error(e.response?.data?.detail || "Gagal memuat rekonsiliasi"); }); }, [load]);

  useEffect(() => {
    (async () => {
      const [s, p, c, a1, a2, a3] = await Promise.all([
        api.get("/sales-agents"), api.get("/products"), api.get("/customers"),
        api.get("/audit-logs", { params: { entity: "opname", limit: 6 } }),
        api.get("/audit-logs", { params: { entity: "adjustment", limit: 6 } }),
        api.get("/audit-logs", { params: { entity: "system_health", limit: 6 } }),
      ]);
      setSalesList(s.data); setProducts(p.data); setCustomers(c.data);
      const merged = [...a1.data, ...a2.data, ...a3.data]
        .sort((x, y) => String(y.created_at).localeCompare(String(x.created_at)))
        .slice(0, 10);
      setAuditLogs(merged);
    })().catch((e) => console.error("Gagal memuat referensi:", e));
  }, [repairResult]);

  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))].sort();

  const showCauses = async () => {
    setCausesLoading(true); setCausesOpen(true);
    try {
      const params = { location };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (location === "armada" && salesId) params.sales_id = salesId;
      if (productId) params.product_id = productId;
      const r = await api.get("/reconciliation/causes", { params });
      setCauses(r.data.rows);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memindai penyebab"); setCausesOpen(false); }
    finally { setCausesLoading(false); }
  };

  const runRepairPlan = async () => {
    try {
      const r = await api.post("/system-health/repair/plan");
      setRepairPlan(r.data); setRepairOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat rencana perbaikan"); }
  };

  const confirmRepair = async () => {
    setRepairing(true);
    try {
      const res = await api.post("/system-health/repair/execute", {});
      const health = await api.post("/system-health/run");
      const fresh = await load();
      setRepairResult({ ...res.data, errors_left: health.data.error_count, variance_after: fresh.variance_value });
      setRepairOpen(false);
      toast.success("Perbaikan selesai & validasi ulang dijalankan");
    } catch (e) { toast.error(e.response?.data?.detail || "Repair gagal"); }
    finally { setRepairing(false); }
  };

  const st = data ? STATUS_UI[data.status] : null;
  const causeBySku = {};
  (causes || []).forEach((c) => { if (c.sku) (causeBySku[c.sku] = causeBySku[c.sku] || []).push(c.penyebab); });

  return (
    <div className="space-y-6" data-testid="page-reconciliation">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Audit</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Neraca & Rekonsiliasi Stok</h1>
        <p className="text-slate-500 text-sm mt-1">Menjawab: berapa selisihnya, transaksi mana penyebabnya, dan apa tindakannya.</p>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="py-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" /></div>
          <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" /></div>
          <div><Label className="text-xs">Lokasi</Label>
            <Select value={location} onValueChange={(v) => { setLocation(v); if (v === "warehouse") setSalesId(""); }}>
              <SelectTrigger data-testid="filter-location"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="armada">Armada / Sales</SelectItem><SelectItem value="warehouse">Gudang</SelectItem></SelectContent>
            </Select>
          </div>
          {location === "armada" && (
            <div><Label className="text-xs">Armada / Sales</Label>
              <Select value={salesId || "__all"} onValueChange={(v) => setSalesId(v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="filter-sales"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__all">Semua Armada</SelectItem>{salesList.map((s) => <SelectItem key={s.sales_id} value={s.sales_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs">Produk</Label>
            <Select value={productId || "__all"} onValueChange={(v) => setProductId(v === "__all" ? "" : v)}>
              <SelectTrigger data-testid="filter-product"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="__all">Semua Produk</SelectItem>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Kategori</Label>
            <Select value={category || "__all"} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
              <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="__all">Semua Kategori</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Pelanggan</Label>
            <Select value={customerId || "__all"} onValueChange={(v) => setCustomerId(v === "__all" ? "" : v)}>
              <SelectTrigger data-testid="filter-customer"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="__all">Semua Pelanggan</SelectItem>{[...customers].sort((a, b) => a.name.localeCompare(b.name, "id")).map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      {data?.customer_filtered && (
        <p className="text-xs text-amber-700 -mt-3" data-testid="customer-filter-note">Filter pelanggan aktif — hanya memengaruhi baris "Barang Terjual"; angka ledger tetap keseluruhan.</p>
      )}

      {/* Status Banner */}
      {data && st && (
        <div data-testid="recon-status" className={`rounded-lg border-2 p-6 flex items-center gap-5 ${st.cls}`}>
          <div className={`h-14 w-14 rounded-full grid place-items-center ${st.iconCls}`}>
            <st.Icon className="h-7 w-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Status Neraca</div>
            <div className={`font-display text-2xl font-bold ${st.textCls}`}>{st.label}</div>
            {data.pending_count > 0 && (
              <div className="text-sm text-amber-700 mt-1" data-testid="pending-opnames-info">
                {data.pending_count} stock opname menunggu persetujuan ({rupiah(data.pending_value)}) — adjustment <b>belum</b> dihitung ke stok.
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Selisih Rekonsiliasi</div>
            <div className={`font-mono text-xl font-bold ${data.variance_value === 0 ? "text-emerald-800" : "text-rose-800"}`} data-testid="recon-variance">
              {rupiah(data.variance_value)}
            </div>
            {data.loss_total > 0 && <div className="text-xs text-rose-700 font-mono" data-testid="recon-loss">Estimasi kerugian: {rupiah(data.loss_total)}</div>}
          </div>
        </div>
      )}

      {/* Persamaan Neraca */}
      {data && (
        <Card>
          <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><Scale className="h-5 w-5 text-slate-500" /> Persamaan Neraca {data.location === "armada" ? "Armada" : "Gudang"}</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Stok Menurut Transaksi (dokumen)</div>
              {data.opening_row && <EqRow label="Saldo Awal Periode" value={data.opening_row.value} qty={data.opening_row.qty} testid="eq-opening" />}
              {data.rows.map((r) => <EqRow key={r.key} label={r.label} value={r.value} qty={r.qty} testid={`eq-${r.key}`} />)}
              <div className="border-t-2 border-slate-300 mt-2 pt-2 flex items-center">
                <div className="text-sm font-bold">= Stok Seharusnya</div>
                <div className="ml-auto text-right">
                  <div className="font-mono font-bold" data-testid="eq-expected-total">{rupiah(data.expected_value)}</div>
                  <div className="text-xs text-slate-500 font-mono">{num(data.expected_qty)} unit</div>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Stok Menurut Ledger (stock movement)</div>
              {data.ledger_opening_value !== null && data.ledger_opening_value !== undefined && (
                <EqRow label="Saldo Awal Ledger" value={data.ledger_opening_value} qty={data.ledger_opening_qty} testid="eq-ledger-opening" />
              )}
              <div className="border-t-2 border-slate-300 mt-2 pt-2 flex items-center">
                <div className="text-sm font-bold">Stok Aktual (Ledger)</div>
                <div className="ml-auto text-right">
                  <div className="font-mono font-bold" data-testid="eq-actual-ledger">{rupiah(data.ledger_value)}</div>
                  <div className="text-xs text-slate-500 font-mono">{num(data.ledger_qty)} unit</div>
                </div>
              </div>
              <div className={`mt-4 rounded-lg border-2 p-4 flex items-center ${data.variance_value === 0 && data.variance_qty === 0 ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}>
                <div className="text-sm font-semibold">Selisih (Ledger − Seharusnya)</div>
                <div className={`ml-auto font-mono font-bold text-lg ${data.variance_value === 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="eq-variance">
                  {rupiah(data.variance_value)} <span className="text-xs font-normal">({num(data.variance_qty)} unit)</span>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={showCauses} data-testid="btn-show-causes"><Search className="h-4 w-4 mr-1" />Lihat Penyebab Selisih</Button>
                {canRepair && <Button className="bg-slate-900 hover:bg-slate-800" onClick={runRepairPlan} data-testid="btn-run-repair"><Wrench className="h-4 w-4 mr-1" />Perbaiki Data</Button>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hasil Repair */}
      {repairResult && (
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="repair-result">
          <div className="font-display font-bold text-emerald-900 mb-2">Hasil Perbaikan Data</div>
          <div className="grid gap-1 text-sm text-emerald-900">
            {repairResult.repaired === 0 && <div>✔ Tidak ada data bermasalah — tidak ada yang diubah.</div>}
            {Object.entries(ACTION_LABEL).map(([k, label]) => repairResult[k] ? <div key={k}>✔ {label}: {repairResult[k]}</div> : null)}
            <div className="font-bold mt-1">
              {Math.abs(repairResult.variance_after) < 1
                ? "✔ Rekalkulasi selesai — Selisih setelah repair: Rp0"
                : `⚠ Masih terdapat selisih ${rupiah(repairResult.variance_after)} — ${repairResult.errors_left} transaksi membutuhkan pemeriksaan manual.`}
            </div>
          </div>
        </div>
      )}

      {/* Detail per Barang */}
      {data && (
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Detail per Barang</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-recon-products">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3">SKU</th><th className="py-2 pr-3">Barang</th><th className="py-2 pr-3">Kategori</th>
                  <th className="py-2 pr-3 text-right">Stok Sistem</th><th className="py-2 pr-3 text-right">Stok Ledger</th>
                  <th className="py-2 pr-3 text-right">Selisih</th><th className="py-2 pr-3 text-right">HPP</th>
                  <th className="py-2 pr-3 text-right">Estimasi Kerugian</th><th className="py-2">Penyebab</th>
                </tr>
              </thead>
              <tbody>
                {data.products.map((p) => (
                  <tr key={p.product_id} className="border-b border-slate-100" data-testid={`recon-row-${p.sku}`}>
                    <td className="py-2 pr-3 font-mono text-xs">{p.sku}</td>
                    <td className="py-2 pr-3">{p.name}</td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{p.category || "—"}</td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {num(p.expected_qty)} {p.unit}
                      {packDisplay(p.expected_qty, p.unit, p.packs) && <div className="text-[10px] text-slate-400">{packDisplay(p.expected_qty, p.unit, p.packs)}</div>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">{num(p.ledger_qty)} {p.unit}</td>
                    <td className={`py-2 pr-3 text-right font-mono font-bold ${p.variance_qty === 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid={`recon-variance-${p.sku}`}>
                      {p.variance_qty > 0 ? `+${num(p.variance_qty)}` : num(p.variance_qty)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">{rupiah(p.cost_price)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-rose-700">{p.loss_value > 0 ? rupiah(p.loss_value) : "—"}</td>
                    <td className="py-2">
                      {p.variance_qty === 0
                        ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">OK</Badge>
                        : <span className="text-xs text-rose-700">{(causeBySku[p.sku] || ["Selisih dokumen vs ledger — klik Lihat Penyebab Selisih"]).join("; ")}</span>}
                    </td>
                  </tr>
                ))}
                {data.products.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Tidak ada data pada filter ini</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Jejak Audit */}
      <Card>
        <CardHeader><CardTitle className="font-display text-lg">Jejak Perubahan (Opname / Adjustment / Perbaikan)</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1" data-testid="recon-audit">
            {auditLogs.map((l) => (
              <div key={l.log_id} className="flex items-center gap-3 text-sm border-b border-slate-100 py-1.5">
                <span className="font-mono text-xs text-slate-500 whitespace-nowrap">{formatDateTime(l.created_at)}</span>
                <span className="font-medium">{l.user_email}</span>
                <Badge className="bg-slate-100 text-slate-700 font-normal">{l.entity} · {l.action}</Badge>
                <span className="text-xs text-slate-500 truncate">{(l.after && (l.after.trx_no || l.after.adj_no)) || (l.before && l.before.trx_no) || ""}</span>
              </div>
            ))}
            {auditLogs.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">Belum ada jejak perubahan</div>}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Penyebab Selisih */}
      <Dialog open={causesOpen} onOpenChange={setCausesOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Penyebab Selisih — Hasil Scan</DialogTitle><DialogDescription>Daftar transaksi & temuan yang menyebabkan selisih stok, dari mesin Pemeriksaan Sistem.</DialogDescription></DialogHeader>
          {causesLoading && <div className="py-8 text-center text-slate-400 text-sm">Memindai seluruh transaksi & ledger…</div>}
          {!causesLoading && causes && (
            <table className="w-full text-sm" data-testid="table-causes">
              <thead>
                <tr className="border-b text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-2">Tanggal</th><th className="py-2 pr-2">Jenis</th><th className="py-2 pr-2">Nomor</th>
                  <th className="py-2 pr-2">Barang</th><th className="py-2 pr-2 text-right">Qty</th><th className="py-2 pr-2 text-right">Nilai</th>
                  <th className="py-2 pr-2">Status</th><th className="py-2 pr-2">Penyebab</th><th className="py-2">Rekomendasi</th>
                </tr>
              </thead>
              <tbody>
                {causes.map((c, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-2 font-mono text-xs whitespace-nowrap">{c.date ? formatDate(c.date) : "—"}</td>
                    <td className="py-2 pr-2 text-xs">{c.jenis}</td>
                    <td className="py-2 pr-2 font-mono text-xs">{c.ref_no || "—"}</td>
                    <td className="py-2 pr-2 text-xs">{c.sku || "—"}</td>
                    <td className="py-2 pr-2 text-right font-mono text-xs">{c.qty != null ? num(c.qty) : "—"}</td>
                    <td className="py-2 pr-2 text-right font-mono text-xs">{c.value != null ? rupiah(c.value) : "—"}</td>
                    <td className="py-2 pr-2">
                      <Badge className={`font-normal ${c.status === "Error" ? "bg-rose-100 text-rose-800" : c.status === "Pending" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{c.status}</Badge>
                    </td>
                    <td className="py-2 pr-2 text-xs">{c.penyebab}</td>
                    <td className="py-2 text-xs text-slate-500">{c.rekomendasi || "—"}</td>
                  </tr>
                ))}
                {causes.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Tidak ditemukan penyebab — seluruh transaksi valid</td></tr>}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Rencana Perbaikan */}
      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Rencana Perbaikan Data</DialogTitle><DialogDescription>Semua perubahan di-backup sebelum ditulis dan dapat dipulihkan dari halaman Pemeriksaan Sistem.</DialogDescription></DialogHeader>
          {repairPlan && (
            <div data-testid="repair-plan-dialog">
              <p className="text-sm text-slate-600 mb-3">
                Sistem akan: scan missing/duplicate/orphan movement, ledger, saldo awal & adjustment pending → memperbaiki yang teridentifikasi (backup penuh sebelum menulis, bisa dipulihkan) → rekalkulasi stok → validasi ulang.
              </p>
              {repairPlan.count === 0
                ? <div className="text-sm text-emerald-700 font-medium">✔ Tidak ada data bermasalah yang teridentifikasi.</div>
                : <div className="space-y-1 text-sm max-h-64 overflow-y-auto">
                    {repairPlan.plans.slice(0, 20).map((pl, i) => (
                      <div key={i} className="border-b border-slate-100 py-1.5 flex gap-2">
                        <Badge className="bg-slate-100 text-slate-700 font-normal shrink-0">{ACTION_LABEL[pl.action] || pl.action}</Badge>
                        <span className="font-mono text-xs">{pl.ref_no || ""}</span>
                        <span className="text-xs text-slate-500 truncate">{pl.reason}</span>
                      </div>
                    ))}
                    {repairPlan.count > 20 && <div className="text-xs text-slate-400">…dan {repairPlan.count - 20} lainnya</div>}
                  </div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepairOpen(false)}>Batal</Button>
            {repairPlan?.count > 0 && <Button className="bg-slate-900 hover:bg-slate-800" onClick={confirmRepair} disabled={repairing} data-testid="btn-confirm-repair">{repairing ? "Memperbaiki…" : `Jalankan Perbaikan (${repairPlan?.count})`}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EqRow({ label, value, qty, testid }) {
  return (
    <div className="flex items-center py-1.5 border-b border-slate-100">
      <div className="text-sm text-slate-700">{label}</div>
      <div className="ml-auto text-right" data-testid={testid}>
        <span className="font-mono text-sm">{rupiah(value)}</span>
        <span className="text-xs text-slate-400 font-mono ml-2">{num(qty)} unit</span>
      </div>
    </div>
  );
}
